import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  GLOBAL_MARKET_CODE,
  marketEntryKey,
  type PreflightMarketCatalogueEntry,
} from "@nexus/contracts";
import { preflightMarketCatalogueQueryOptions } from "@/lib/queries";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  SearchableCombobox,
  type ComboboxOptionGroup,
} from "@nexus/ui";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import { useCreateMarket, useDeleteMarket } from "../../hooks/usePreflightMarkets";
import { sortMarketsByKindThenName } from "../../lib/market-order";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import type { PreflightMarketWithCount } from "../../types/preflight.types";

/** Conflict code from `GatewayError.marketAlreadyAdded` — the only duplicate a catalogue admits. */
const ALREADY_ADDED_CODE = "MARKET_ALREADY_ADDED";

export interface ManageMarketsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markets: PreflightMarketWithCount[];
  /**
   * A removal is reported rather than resolved here, so the surface that owns the
   * market scope can fall a removed selection back to all-markets (R10).
   */
  onMarketRemoved?: (marketId: string) => void;
}

/**
 * The controlled market list: pick an entry from the DB catalogue and remove
 * one that no report uses (R6, R7, R9). There is no name or code field — both come
 * from the catalogue entry, so a market cannot be mistyped. The create mutation opts
 * out of the global error toast so a conflict is surfaced here instead.
 */
export function ManageMarketsDialog({
  open,
  onOpenChange,
  markets,
  onMarketRemoved,
}: ManageMarketsDialogProps) {
  const { t } = useTranslation();
  const createMutation = useCreateMarket();
  const deleteMutation = useDeleteMarket();
  const catalogueQuery = useQuery({
    ...preflightMarketCatalogueQueryOptions(),
    enabled: open,
  });
  const catalogue = catalogueQuery.data ?? [];

  const [formError, setFormError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const heldKeys = useMemo(() => new Set(markets.map(marketEntryKey)), [markets]);
  const orderedMarkets = useMemo(
    () => sortMarketsByKindThenName(markets),
    [markets],
  );

  const groups = useMemo<ComboboxOptionGroup<PreflightMarketCatalogueEntry>[]>(() => {
    const byKind = (kind: PreflightMarketCatalogueEntry["kind"]) =>
      catalogue.filter((entry) => entry.kind === kind);

    // Global leads without a heading of its own — a single row needs no section.
    return [
      { items: byKind("GLOBAL") },
      {
        heading: t("preflight.markets.groupContinents", "Continents"),
        items: byKind("CONTINENT"),
      },
      {
        heading: t("preflight.markets.groupCountries", "Countries"),
        items: byKind("COUNTRY"),
      },
    ];
  }, [catalogue, t]);

  function handleSelect(entry: PreflightMarketCatalogueEntry) {
    setFormError(null);
    setPendingKey(marketEntryKey(entry));

    createMutation.mutate(
      { kind: entry.kind, code: entry.code },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.code === ALREADY_ADDED_CODE) {
            setFormError(
              t(
                "preflight.markets.error.alreadyAdded",
                "{{market}} is already in your list.",
                { market: entry.name },
              ),
            );
            return;
          }
          const message = getLocalUserFacingApiMessage(error);
          if (message) setFormError(message);
        },
        onSettled: () => setPendingKey(null),
      },
    );
  }

  function handleRemove(marketId: string) {
    setFormError(null);
    deleteMutation.mutate(marketId, {
      onSuccess: () => onMarketRemoved?.(marketId),
      onError: (error) => {
        const message = getLocalUserFacingApiMessage(error);
        if (message) setFormError(message);
      },
    });
  }

  const pickerLabel = t("preflight.markets.pickerLabel", "Add a market");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="form" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("preflight.markets.manageTitle", "Manage markets")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "preflight.markets.manageDescription",
              "Markets scope your reports. Pick a country, a continent or Global — each can be added once.",
            )}
          </DialogDescription>
        </DialogHeader>

        {orderedMarkets.length > 0 ? (
          <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {orderedMarkets.map((market) => (
              <MarketRow
                key={market.id}
                market={market}
                busy={deleteMutation.isPending}
                onRemove={handleRemove}
              />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-xs">
            {t("preflight.markets.manageEmpty", "No markets yet — add the first one below.")}
          </p>
        )}

        <SearchableCombobox
          groups={groups}
          getKey={marketEntryKey}
          getLabel={(entry) => entry.name}
          // Typing a code reaches the entry while the row keeps showing its name (KTD7).
          getSearchText={(entry) => `${entry.name} ${entry.code}`}
          getDisabled={(entry) => heldKeys.has(marketEntryKey(entry))}
          onSelect={handleSelect}
          value={null}
          selectingKey={pendingKey}
          // A pending or failed fetch must not read as an empty catalogue: the
          // combobox shows its own loading skeletons and error state instead.
          loading={catalogueQuery.isLoading}
          error={
            catalogueQuery.isError
              ? t("preflight.markets.pickerError", "Couldn't load markets. Try again.")
              : null
          }
          closeOnSelect={false}
          placeholder={pickerLabel}
          ariaLabel={pickerLabel}
          searchPlaceholder={t(
            "preflight.markets.pickerSearch",
            "Search countries, continents or Global…",
          )}
          emptyMessage={t(
            "preflight.markets.pickerEmpty",
            "No catalogue entry matches.",
          )}
          triggerIcon="plus"
          // The badge reads the held set, not `isDisabled`: the primitive also
          // disables every row while a create is in flight, which would otherwise
          // claim the whole catalogue is already held.
          renderItem={(entry) => (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <MarketIcon kind={entry.kind} code={entry.code} name={entry.name} />
              <span className="min-w-0 truncate">{entry.name}</span>
              {entry.code === GLOBAL_MARKET_CODE ? null : (
                <span className="text-muted-foreground font-mono text-xs">
                  {entry.code}
                </span>
              )}
              {heldKeys.has(marketEntryKey(entry)) ? (
                <span className="text-muted-foreground ml-auto text-xs">
                  {t("preflight.markets.alreadyAdded", "Already added")}
                </span>
              ) : null}
            </span>
          )}
        />

        {formError && (
          <p role="alert" className="text-error text-xs">
            {formError}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {t("preflight.markets.manageDone", "Done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MarketRowProps {
  market: PreflightMarketWithCount;
  busy: boolean;
  onRemove: (marketId: string) => void;
}

function MarketRow({ market, busy, onRemove }: MarketRowProps) {
  const { t } = useTranslation();
  // Only canonical GLOBAL:GLOBAL is protected: it is where a removed market's
  // reports land, so the API keeps it for every tenant and offering Remove would
  // only ever produce a 409. Regional GLOBAL-kind entries (MENA, NORDICS, …) are
  // ordinary assignments the backend's deleteIfUnused permits, so they stay
  // removable when unused.
  const isCanonicalGlobal =
    market.kind === "GLOBAL" && market.code === GLOBAL_MARKET_CODE;
  const isInUse = market.reportCount > 0;
  const reasonId = `manage-markets-reason-${market.id}`;
  const reason = isCanonicalGlobal
    ? t("preflight.markets.globalKept", "Always available")
    : isInUse
      ? t("preflight.markets.inUse", {
          count: market.reportCount,
          defaultValue_one: "{{count}} report uses this market",
          defaultValue_other: "{{count}} reports use this market",
        })
      : null;

  return (
    <li className="hover:bg-muted flex items-center gap-3 rounded-md px-2 py-1.5">
      <MarketIcon kind={market.kind} code={market.code} name={market.name} />
      <span className="min-w-0 flex-1 truncate text-sm">{market.name}</span>
      <span className="text-muted-foreground font-mono text-xs">{market.code}</span>
      {reason && (
        <span id={reasonId} className="text-muted-foreground text-xs">
          {reason}
        </span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={isCanonicalGlobal || isInUse || busy}
        aria-label={t("preflight.markets.removeAria", "Remove {{name}}", {
          name: market.name,
        })}
        aria-describedby={reason ? reasonId : undefined}
        onClick={() => onRemove(market.id)}
      >
        <Icon name="trash-2" size="sm" />
      </Button>
    </li>
  );
}
